import { type ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

// 2-role model post 2026-06-29 RBAC collapse. Legacy values (viewer,
// resource_manager, senior_consultant) collapse to `consultant` in the
// effectiveRole derivation below.
export type Role = 'firm_admin' | 'consultant';

interface MeResponse {
  user_id?: string;
  role?: Role | string;
}

/**
 * Route guard — blocks direct URL access for non-allowed roles.
 *
 * Wrap admin-only routes in <RequireRole allow={['firm_admin']}>…</RequireRole>.
 * Until /auth/me resolves we render a tiny placeholder (no flicker through
 * to the protected content even for a frame).
 *
 * Backend enforcement on each endpoint is the actual security boundary.
 * This wrapper exists for UX — non-admins should never SEE admin URLs
 * succeed and then have actions silently fail.
 */
export function RequireRole({ allow, children }: { allow: Role[]; children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/v1/auth/me', { credentials: 'include' });
        if (!cancelled) setMe(r.ok ? await r.json() : { role: undefined });
      } catch {
        if (!cancelled) setMe({ role: undefined });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{
        padding: 32,
        fontFamily: 'var(--serif, Georgia, serif)',
        fontStyle: 'italic',
        color: 'var(--text-muted, #636670)',
      }}>
        Checking access…
      </div>
    );
  }

  // Collapse any historical role string onto one of the two supported roles.
  const effectiveRole: Role | undefined = !me?.role
    ? undefined
    : (me.role === 'firm_admin' ? 'firm_admin' : 'consultant');

  if (!effectiveRole || !allow.includes(effectiveRole)) {
    // Fallback target depends on what the caller can reach.
    //   firm_admin   → /sales       (admin landing)
    //   consultant   → /engagements (universal workspace landing)
    const fallback = effectiveRole === 'firm_admin' ? '/sales' : '/engagements';
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}

export default RequireRole;
